import { faX } from '@fortawesome/pro-light-svg-icons'
import { Modify } from '@teinor/erp'
import { WCalSpecialModelType } from '@teinor/erp/types/company/WCalParent/WCal/WCalSpecial'
import { useState, useMemo, useCallback, useEffect } from 'react'
import { useParams } from 'react-router'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { BigTitleAndRightLayout } from '../../../../../../../baseComponents/Layout/TitleAndRightLayout'
import PermissionChecker from '../../../../../../../baseComponents/PermissionChecker'
import { TwinBigCalendarEvents } from '../../../../../../../baseComponents/TwinBigCalendar/types'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { EventsWorkingCalendarProps } from '../../../../../../../baseComponents/WorkingCalendar'
import WhiteBoxCalendarEvents from '../../../../../../../specificComponents/WCal/WhiteBoxCalendarEvents'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { displayFormatedHourFromMins } from '../../../../../../../utils/globals/date'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import useMonthDictionary from '../../../../../../../utils/hooks/useMonthDictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { appStore } from '../../../../../../../utils/reducers'
import { setVariablesToCurrentPage } from '../../../../../../../utils/reducers/reduxDispatch'
import ModalCreateWCSpecialDays from './ModalCreateWCSpecialDays'
import ModalDeleteWCSpecialDays from './ModalDeleteWCSpecialDays'

interface WorkingCalendarSpecialDaysProps extends WorkingCalendarSpecialDaysLogicProps { }

const WorkingCalendarSpecialDays: React.FC<WorkingCalendarSpecialDaysProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, getData, events } = useWorkingCalendarSpecialDaysLogic({ userPermissions })
    return (
        <BigTitleAndRightLayout
            title={t('specialDays', 'Días festivos')}
            RightHeader={
                <PermissionChecker userPermissions={userPermissions} permission='create'>
                    <ButtonPrimary onClick={() => setOpenModal({ type: 'create', allRowData: {} })}><TwinTrans transKey={'addSpecialDay'}>Añadir Día Especial</TwinTrans></ButtonPrimary>
                </PermissionChecker>
            }>
            <WCSpecialDaysParentCalendar events={events} getData={getData} />
            {openModal?.type === 'create' ? <ModalCreateWCSpecialDays setOpened={() => setOpenModal(null)} getData={getData} /> : null}
            {openModal?.type === 'delete' ? <ModalDeleteWCSpecialDays setOpened={() => setOpenModal(null)} onSubmit={getData} id={openModal.allRowData.id} /> : null}
        </BigTitleAndRightLayout>
    )
}
interface WorkingCalendarSpecialDaysLogicProps extends ComponentWithPermissions { }

export const useWorkingCalendarSpecialDaysLogic = ({ userPermissions }: WorkingCalendarSpecialDaysLogicProps) => {
    const { id, year } = useParams()
    const [events, setEvents] = useState<EventsWorkingCalendarSpecial>(null)
    const { t } = useTwinTranslation()
    const months = useMonthDictionary()
    const userPermissionsStr = JSON.stringify(userPermissions)
    const { openModal, setOpenModal } = useEditDeleteModal()
    const montshJSON = JSON.stringify(months)
    const myYear = useMemo(() => parseInt(year || '0'), [year])
    const parseDateISOToString = useCallback((dateISO: Date) => {
        const myMonths = JSON.parse(montshJSON)
        const dateString = dateISO.getDate() + '  ' + myMonths[dateISO.getMonth()]
        return dateString
    }, [montshJSON])

    const freeDay = t('freeDay', 'Día festivo')
    const parseDataToEvents = useCallback((data: WCalSpecialModelType) => {
        const userPermissionsParsed = JSON.parse(userPermissionsStr)
        const wCalSpecialDays = data?.WCalSpecialDays
        const eventsParsed: TwinBigCalendarEvents = []
        if (wCalSpecialDays) {
            for (const element of wCalSpecialDays) {
                const date = new Date(element.day)
                const dayParsed = parseDateISOToString(date)
                let schedule = freeDay
                if(element.schedule){
                    const parsedSchedule = JSON.parse(element.schedule)
                    if(parsedSchedule.workingTime && parsedSchedule.workingTime.length){
                        schedule = ''
                        for (const mins of parsedSchedule.workingTime) {
                            schedule += `${displayFormatedHourFromMins(mins[0])} - ${displayFormatedHourFromMins(mins[1])} \n`
                        }
                    } 
                }
                eventsParsed.push({
                    id: element.id,
                    title: dayParsed,
                    allDay: false,
                    start: date,
                    subtitle: schedule,
                    color: element.color,
                    rightSide: (
                        <PermissionChecker userPermissions={userPermissionsParsed} permission='delete'>
                            <TwinIcon icon={faX} className='cursor-pointer ml-auto' onClick={() => setOpenModal({ type: 'delete', allRowData: { id: element.id } })}></TwinIcon>
                        </PermissionChecker>
                    )
                })
            }
            return eventsParsed
        }
        return null
    }, [parseDateISOToString, setOpenModal, userPermissionsStr, freeDay])

    const getData = useCallback(async () => {
        const currentPageVariables = appStore.getState()?.currentPage?.variables
        setEvents(null)
        const result = await twinFetchPostJSON('/api/app/workingCalendar/workingCalendarSpecial/getWCalSpecialEventsForUpdate', {
            id,
            year: myYear
        })
        if (result) {
            let parsedEvents: EventsWorkingCalendarSpecial = null
            if (result?.msg === 'LAST_YEAR_EVENTS') {
                parsedEvents = 'LAST_YEAR_EVENTS'
            } else {
                parsedEvents = parseDataToEvents(result)
            }
            await setEvents(parsedEvents)
            if (!currentPageVariables?.length) {
                setVariablesToCurrentPage([result.name])
            }
        }
    }, [parseDataToEvents, id, myYear, setEvents])

    useEffect(() => {
        getData()
    }, [getData])


    return { openModal, setOpenModal, getData, events }
}

type EventsWorkingCalendarSpecial = TwinBigCalendarEvents | null | 'LAST_YEAR_EVENTS'
type WCSpecialDaysParentCalendarProps = Modify<EventsWorkingCalendarProps, WCSpecialDaysParentCalendarLogicProps & {
    events: EventsWorkingCalendarSpecial
}>

const WCSpecialDaysParentCalendar: React.FC<WCSpecialDaysParentCalendarProps> = ({ events, ...logic }) => {
    const { handlePreviousYearEvents } = WCSpecialDaysParentCalendarLogic({ ...logic })
    return (
        <WhiteBoxCalendarEvents events={events} calendarProps={{ defaultView: 'year' }} handlePreviousYearEvents={handlePreviousYearEvents} />
    )
}

interface WCSpecialDaysParentCalendarLogicProps {
    getData: () => Promise<void>
}

const WCSpecialDaysParentCalendarLogic = ({ getData }: WCSpecialDaysParentCalendarLogicProps) => {
    const { id, year } = useParams()
    const handlePreviousYearEvents = useCallback(async () => {
        const myYear = parseInt(year || '0')
        let result = await twinFetchPostJSON('/api/app/workingCalendar/workingCalendarSpecial/copyWCalSpecialDaysFromLastYear', {
            id,
            year: myYear
        })
        if (result) {
            getData()
        }
    }, [getData, year, id])
    return { handlePreviousYearEvents }
}

export default WorkingCalendarSpecialDays
